<template>
	<v-dialog
		:value="value"
		@input="$emit('input', $event)"
		max-width="1250px"
		scrollable
		transition="dialog-bottom-transition"
		persistent
	>
		<v-card class="elevation-16 rounded-xl overflow-hidden audit-modal-card">
			<!-- Header Modal con Gradiente Oscuro Premium -->
			<v-card-title class="audit-header d-flex justify-space-between align-center px-5 py-4">
				<div class="d-flex align-center">
					<v-avatar color="primary lighten-4" size="44" class="mr-3 elevation-2">
						<v-icon color="primary darken-3" medium>mdi-source-branch</v-icon>
					</v-avatar>
					<div>
						<div class="d-flex align-center">
							<h3 class="white--text text-h6 font-weight-bold mb-0 mr-3">
								Historial Auditable de Cambios (Git Diff)
							</h3>
							<v-chip
								v-if="auditList.length > 0"
								small
								color="primary"
								class="font-weight-bold white--text px-2"
							>
								{{ auditList.length }} {{ auditList.length === 1 ? 'Versión' : 'Versiones' }}
							</v-chip>
						</div>
						<span class="grey--text text--lighten-2 caption font-weight-medium">
							Acopio #{{ pseId || (itemHeader ? itemHeader.PseID : '') }} 
							<span v-if="itemHeader && itemHeader.PrdName">| {{ itemHeader.PrdName }}</span>
						</span>
					</div>
				</div>

				<div class="d-flex align-center">
					<!-- Toggle Modo de Vista (Tabla vs Línea de Tiempo Git) -->
					<v-btn-toggle
						v-model="viewMode"
						mandatory
						dense
						class="mr-3 rounded-pill bg-slate-800 border-slate"
					>
						<v-btn value="timeline" small text class="text-none white--text">
							<v-icon x-small left>mdi-git</v-icon>
							Modo Git Diff
						</v-btn>
						<v-btn value="table" small text class="text-none white--text">
							<v-icon x-small left>mdi-table</v-icon>
							Tabla
						</v-btn>
					</v-btn-toggle>

					<v-btn icon dark @click="closeModal" class="close-btn">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</div>
			</v-card-title>

			<!-- Cuerpo Principal -->
			<v-card-text class="pa-5 bg-grey-lighten">
				<!-- Spinner de Carga -->
				<div v-if="loading" class="text-center py-10">
					<v-progress-circular
						indeterminate
						color="primary"
						size="50"
						width="4"
					></v-progress-circular>
					<div class="subtitle-2 grey--text text--darken-1 font-weight-medium mt-3">
						Cargando historial auditable...
					</div>
				</div>

				<!-- Sin Registros -->
				<v-card
					v-else-if="filteredAuditList.length === 0"
					class="pa-8 text-center rounded-xl border-dashed elevation-0 bg-white"
				>
					<v-avatar color="grey lighten-4" size="64" class="mb-3">
						<v-icon color="grey darken-1" large>mdi-git-commit</v-icon>
					</v-avatar>
					<h4 class="subtitle-1 grey--text text--darken-2 font-weight-bold mb-1">
						Sin registros de auditoría
					</h4>
					<p class="caption grey--text text--darken-1 mb-0">
						No se encontraron modificaciones previas para esta programación de acopio.
					</p>
				</v-card>

				<!-- MODO 1: LÍNEA DE TIEMPO CON GIT DIFF (UX/UI Premium) -->
				<div v-else-if="viewMode === 'timeline'" class="timeline-container">
					<v-timeline dense align-top class="custom-timeline">
						<v-timeline-item
							v-for="(item, index) in filteredAuditList"
							:key="item.PsaID || index"
							:color="getTimelineDotColor(index)"
							small
							fill-dot
						>
							<template v-slot:icon>
								<v-icon x-small color="white">
									{{ getTimelineIcon(index) }}
								</v-icon>
							</template>

							<v-card
								class="elevation-2 rounded-xl mb-4 overflow-hidden border-card"
								:class="{ 'card-highlight-changed': getChangedCount(index) > 0 }"
							>
								<!-- Card Header con Información de Usuario, Fecha y Diff Badge -->
								<div class="d-flex align-center justify-space-between px-4 py-3 border-card-header bg-white flex-wrap gap-2">
									<div class="d-flex align-center">
										<v-avatar color="primary lighten-5" size="34" class="mr-2 border-primary-light">
											<v-icon color="primary" small>mdi-account-edit</v-icon>
										</v-avatar>
										<div>
											<span class="subtitle-2 font-weight-bold grey--text text--darken-3 d-block lh-1">
												{{ item.UsrUpdateName || 'Usuario Desconocido' }}
											</span>
											<span class="caption grey--text text--darken-1">
												ID Usuario: #{{ item.UsrUpdateID || item.UsrCreateID || '-' }}
											</span>
										</div>
									</div>

									<div class="d-flex align-center">
										<!-- Diff Counter Badge -->
										<v-chip
											v-if="getChangedCount(index) > 0"
											x-small
											color="amber darken-3"
											class="white--text font-weight-bold mr-2 px-2 shadow-chip"
										>
											<v-icon x-small left>mdi-file-document-edit-outline</v-icon>
											{{ getChangedCount(index) }} {{ getChangedCount(index) === 1 ? 'cambio' : 'cambios' }}
										</v-chip>
										<v-chip
											v-else-if="index === auditList.length - 1"
											x-small
											color="grey darken-2"
											class="white--text font-weight-bold mr-2 px-2"
										>
											<v-icon x-small left>mdi-plus-circle-outline</v-icon>
											Registro Inicial
										</v-chip>

										<!-- Estado Badge -->
										<v-chip
											x-small
											label
											:color="getStatusColor(item.PseState)"
											class="white--text font-weight-bold mr-3 px-2"
										>
											{{ item.PseStateName || 'Borrador' }}
										</v-chip>

										<div class="text-right">
											<div class="caption font-weight-bold primary--text">
												<v-icon x-small color="primary" class="mr-1">mdi-calendar-clock</v-icon>
												{{ formatDateTime(item.SecUpdate || item.SecCreate) }}
											</div>
											<span class="caption-xs grey--text">
												Versión #{{ auditList.length - index }} (PsaID: {{ item.PsaID }})
											</span>
										</div>
									</div>
								</div>

								<v-divider></v-divider>

								<!-- SECCIÓN PLANA, ESPACIOSA Y MODERNA DE CAMBIOS (SIN CARDS ANIDADAS) -->
								<div v-if="getChangedCount(index) > 0" class="flat-diff-wrapper pa-4 bg-slate-100 border-b">
									<div class="d-flex align-center justify-space-between mb-3">
										<div class="caption font-weight-bold text-uppercase grey--text text--darken-3 d-flex align-center">
											<v-icon x-small color="amber darken-3" class="mr-2">mdi-pencil-ruler</v-icon>
											Modificaciones en esta versión:
										</div>
										<v-chip x-small color="amber darken-3" class="white--text font-weight-bold px-2">
											{{ getChangedCount(index) }} {{ getChangedCount(index) === 1 ? 'campo cambiado' : 'campos cambiados' }}
										</v-chip>
									</div>

									<!-- Lista Plana Espaciosa sin Cards Anidadas -->
									<div class="flat-diff-container rounded-xl bg-white pa-3 border-subtle">
										<div
											v-for="(diff, dIdx) in getDiffList(index)"
											:key="diff.field"
											class="flat-diff-row d-flex align-center justify-space-between py-3.5 px-4 my-1 rounded-lg"
											:class="{ 'border-b-flat': dIdx < getDiffList(index).length - 1 }"
										>
											<!-- Nombre del Campo -->
											<div class="caption font-weight-bold grey--text text--darken-3 d-flex align-center">
												<span class="dot-indicator mr-2.5"></span>
												<span class="subtitle-2 font-weight-bold grey--text text--darken-3">{{ diff.label }}</span>
											</div>

											<!-- Comparativa Plana: ANTES vs AHORA con Espacio Holgado -->
											<div class="d-flex align-center caption flex-wrap">
												<!-- Valor Anterior -->
												<div class="flat-box-old px-4 py-1.5 rounded-pill mr-2 shadow-xs">
													<span class="label-tiny red--text text--darken-4 font-weight-bold mr-1.5">ANTES:</span>
													<span class="red--text text--darken-4 text-strike font-weight-medium">{{ diff.oldVal }}</span>
												</div>

												<v-icon small color="grey darken-1" class="mx-2">mdi-arrow-right</v-icon>

												<!-- Nuevo Valor -->
												<div class="flat-box-new px-4 py-1.5 rounded-pill ml-2 shadow-xs">
													<span class="label-tiny green--text text--darken-4 font-weight-bold mr-1.5">AHORA:</span>
													<span class="green--text text--darken-4 font-weight-bold">{{ diff.newVal }}</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								<!-- Detalles de la Modificación en Secciones Completas -->
								<div class="pa-4 bg-white">
									<v-row dense>
										<!-- Bloque 1: Transporte y Conductor -->
										<v-col cols="12" md="4">
											<div
												class="pa-3 rounded-lg border-section bg-light-blue"
												:class="{ 'field-modified-highlight': isBlockModified(index, ['TptEnrollment', 'TptDriverName', 'NumDocDriver', 'LicenseDriver']) }"
											>
												<div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center justify-space-between">
													<span class="d-flex align-center">
														<v-icon x-small color="info" class="mr-1">mdi-truck</v-icon>
														Transporte y Conductor
													</span>
													<v-chip
														v-if="isBlockModified(index, ['TptEnrollment', 'TptDriverName', 'NumDocDriver', 'LicenseDriver'])"
														x-small
														color="amber darken-3"
														class="white--text font-weight-bold px-1.5"
													>
														MODIFICADO
													</v-chip>
												</div>

												<div class="caption grey--text text--darken-2 mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'TptEnrollment') }">
													<strong>Placa:</strong> 
													<span class="font-weight-bold primary--text ml-1">{{ item.TptEnrollment || 'Sin Placa' }}</span>
												</div>
												<div class="caption grey--text text--darken-2 mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'TptDriverName') }">
													<strong>Conductor:</strong> {{ item.TptDriverName || 'No asignado' }}
												</div>
												<div class="caption grey--text text--darken-1" :class="{ 'text-changed-item': isFieldChanged(index, 'NumDocDriver') || isFieldChanged(index, 'LicenseDriver') }">
													<strong>Doc / Licencia:</strong> 
													{{ item.NumDocDriver || '-' }} / {{ item.LicenseDriver || '-' }}
												</div>
											</div>
										</v-col>

										<!-- Bloque 2: Cargas y Cantidades -->
										<v-col cols="12" md="4">
											<div
												class="pa-3 rounded-lg border-section bg-light-green"
												:class="{ 'field-modified-highlight': isBlockModified(index, ['PseQuantityNegotiated', 'PseQuantityJabas', 'PseStowageQuantity']) }"
											>
												<div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center justify-space-between">
													<span class="d-flex align-center">
														<v-icon x-small color="success" class="mr-1">mdi-package-variant-closed</v-icon>
														Cantidades y Carga
													</span>
													<v-chip
														v-if="isBlockModified(index, ['PseQuantityNegotiated', 'PseQuantityJabas', 'PseStowageQuantity'])"
														x-small
														color="amber darken-3"
														class="white--text font-weight-bold px-1.5"
													>
														MODIFICADO
													</v-chip>
												</div>

												<div class="d-flex justify-space-between caption mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'PseQuantityNegotiated') }">
													<span class="grey--text text--darken-2">Jabas Negociadas:</span>
													<span class="font-weight-bold">{{ item.PseQuantityNegotiated || 0 }}</span>
												</div>
												<div class="d-flex justify-space-between caption mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'PseQuantityJabas') }">
													<span class="grey--text text--darken-2">Cant. Real Jabas:</span>
													<span class="font-weight-bold green--text text--darken-2">{{ item.PseQuantityJabas || 0 }}</span>
												</div>
												<div class="d-flex justify-space-between caption" :class="{ 'text-changed-item': isFieldChanged(index, 'PseStowageQuantity') }">
													<span class="grey--text text--darken-2">Cant. Estibas:</span>
													<span class="font-weight-bold">{{ item.PseStowageQuantity || 0 }}</span>
												</div>
											</div>
										</v-col>

										<!-- Bloque 3: Tarifas y Precios (Decimales 14,6) -->
										<v-col cols="12" md="4">
											<div
												class="pa-3 rounded-lg border-section bg-light-amber"
												:class="{ 'field-modified-highlight': isBlockModified(index, ['PsePriceStowageQuantity', 'PsePrice', 'PseFalseFreight']) }"
											>
												<div class="caption font-weight-bold grey--text text--darken-3 mb-2 d-flex align-center justify-space-between">
													<span class="d-flex align-center">
														<v-icon x-small color="amber darken-3" class="mr-1">mdi-currency-usd</v-icon>
														Precios y Falso Flete
													</span>
													<v-chip
														v-if="isBlockModified(index, ['PsePriceStowageQuantity', 'PsePrice', 'PseFalseFreight'])"
														x-small
														color="amber darken-3"
														class="white--text font-weight-bold px-1.5"
													>
														MODIFICADO
													</v-chip>
												</div>

												<div class="d-flex justify-space-between caption mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'PsePriceStowageQuantity') }">
													<span class="grey--text text--darken-2">Precio Estibas:</span>
													<span class="font-weight-bold">{{ formatCurrency(item.PsePriceStowageQuantity) }}</span>
												</div>
												<div class="d-flex justify-space-between caption mb-1" :class="{ 'text-changed-item': isFieldChanged(index, 'PsePrice') }">
													<span class="grey--text text--darken-2">Precio Unitario:</span>
													<span class="font-weight-bold primary--text">{{ formatCurrency(item.PsePrice) }}</span>
												</div>
												<div class="d-flex justify-space-between caption" :class="{ 'text-changed-item': isFieldChanged(index, 'PseFalseFreight') }">
													<span class="grey--text text--darken-2">Falso Flete:</span>
													<span class="font-weight-bold red--text text--darken-1">{{ formatCurrency(item.PseFalseFreight) }}</span>
												</div>
											</div>
										</v-col>

										<!-- Bloque 4: Observaciones / Descripción (si existe) -->
										<v-col cols="12" v-if="item.PseDescription" class="mt-2">
											<div
												class="pa-2.5 rounded-lg border-dashed bg-amber-lighten"
												:class="{ 'field-modified-highlight': isFieldChanged(index, 'PseDescription') }"
											>
												<div class="caption font-weight-bold grey--text text--darken-3 d-flex align-center justify-space-between">
													<span class="d-flex align-center">
														<v-icon x-small color="warning" class="mr-1">mdi-comment-text-outline</v-icon>
														Observación / Descripción:
													</span>
													<v-chip
														v-if="isFieldChanged(index, 'PseDescription')"
														x-small
														color="amber darken-3"
														class="white--text font-weight-bold px-1.5"
													>
														MODIFICADO
													</v-chip>
												</div>
												<p class="caption grey--text text--darken-2 mb-0 italic-text pl-4">
													"{{ item.PseDescription }}"
												</p>
											</div>
										</v-col>
									</v-row>
								</div>
							</v-card>
						</v-timeline-item>
					</v-timeline>
				</div>

				<!-- MODO 2: TABLA DETALLADA -->
				<div v-else>
					<v-data-table
						:headers="headersTable"
						:items="filteredAuditList"
						class="elevation-1 rounded-xl bg-white border-subtle overflow-hidden"
						dense
						:items-per-page="10"
					>
						<template v-slot:item.SecUpdate="{ item }">
							<span class="caption font-weight-medium primary--text">
								{{ formatDateTime(item.SecUpdate || item.SecCreate) }}
							</span>
						</template>

						<template v-slot:item.UsrUpdateName="{ item }">
							<div class="d-flex align-center py-1">
								<v-avatar color="primary lighten-5" size="24" class="mr-1.5">
									<v-icon color="primary" x-small>mdi-account</v-icon>
								</v-avatar>
								<span class="caption font-weight-bold grey--text text--darken-3">
									{{ item.UsrUpdateName || 'Desconocido' }}
								</span>
							</div>
						</template>

						<template v-slot:item.PseStateName="{ item }">
							<v-chip
								x-small
								label
								:color="getStatusColor(item.PseState)"
								class="white--text font-weight-bold px-2"
							>
								{{ item.PseStateName || 'Borrador' }}
							</v-chip>
						</template>

						<template v-slot:item.TptEnrollment="{ item }">
							<span class="caption font-weight-bold text-uppercase primary--text">
								{{ item.TptEnrollment || '-' }}
							</span>
						</template>

						<template v-slot:item.PsePriceStowageQuantity="{ item }">
							<span class="caption font-weight-bold">
								{{ formatCurrency(item.PsePriceStowageQuantity) }}
							</span>
						</template>

						<template v-slot:item.PsePrice="{ item }">
							<span class="caption font-weight-bold primary--text">
								{{ formatCurrency(item.PsePrice) }}
							</span>
						</template>

						<template v-slot:item.PseFalseFreight="{ item }">
							<span class="caption font-weight-bold red--text text--darken-1">
								{{ formatCurrency(item.PseFalseFreight) }}
							</span>
						</template>
					</v-data-table>
				</div>
			</v-card-text>

			<v-divider></v-divider>

			<!-- Footer del Modal -->
			<v-card-actions class="px-5 py-3 bg-white d-flex justify-space-between align-center">
				<div class="caption grey--text text--darken-1">
					Mostrando <strong>{{ filteredAuditList.length }}</strong> de <strong>{{ auditList.length }}</strong> registros de auditoría
				</div>

				<v-btn
					small
					color="grey darken-2"
					outlined
					class="text-none font-weight-bold rounded-pill px-4"
					@click="closeModal"
				>
					<v-icon left x-small>mdi-close</v-icon>
					Cerrar
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import _sProgrammingStorageService from "@/services/Technicalassistance/ProgrammingStorageService";

export default {
	name: "ProgrammingStorageAuditModal",

	props: {
		value: {
			type: Boolean,
			default: false
		},
		pseId: {
			type: [Number, String],
			default: 0
		},
		itemHeader: {
			type: Object,
			default: () => ({})
		}
	},

	data: () => ({
		loading: false,
		viewMode: "timeline",
		searchQuery: "",
		onlyShowChanged: false,
		auditList: [],
		headersTable: [
			{ text: "#", value: "PsaID", width: 50 },
			{ text: "Fecha y Hora", value: "SecUpdate", width: 150 },
			{ text: "Usuario Responsable", value: "UsrUpdateName", width: 170 },
			{ text: "Estado", value: "PseStateName", width: 100 },
			{ text: "Placa", value: "TptEnrollment", width: 90 },
			{ text: "Conductor", value: "TptDriverName", width: 150 },
			{ text: "Jabas Neg.", value: "PseQuantityNegotiated", width: 90 },
			{ text: "Jabas Real", value: "PseQuantityJabas", width: 90 },
			{ text: "Estibas", value: "PseStowageQuantity", width: 80 },
			{ text: "Precio Estibas", value: "PsePriceStowageQuantity", width: 110 },
			{ text: "Precio", value: "PsePrice", width: 90 },
			{ text: "Falso Flete", value: "PseFalseFreight", width: 100 },
			{ text: "Observación", value: "PseDescription", width: 180 }
		]
	}),

	computed: {
		filteredAuditList() {
			let list = this.auditList;
			
			if (this.onlyShowChanged) {
				list = list.filter((item, index) => {
					// Always keep initial version or any version with changed count > 0
					return index === list.length - 1 || this.getChangedCount(index) > 0;
				});
			}

			if (!this.searchQuery) return list;
			
			const q = this.searchQuery.toLowerCase();
			return list.filter(item => {
				return (
					(item.UsrUpdateName && item.UsrUpdateName.toLowerCase().includes(q)) ||
					(item.TptEnrollment && item.TptEnrollment.toLowerCase().includes(q)) ||
					(item.TptDriverName && item.TptDriverName.toLowerCase().includes(q)) ||
					(item.PseDescription && item.PseDescription.toLowerCase().includes(q)) ||
					(item.PseStateName && item.PseStateName.toLowerCase().includes(q))
				);
			});
		}
	},

	watch: {
		value(val) {
			if (val) {
				this.fetchAuditList();
			}
		}
	},

	methods: {
		fetchAuditList() {
			const id = this.pseId || (this.itemHeader ? this.itemHeader.PseID : 0);
			if (!id || id <= 0) {
				this.auditList = [];
				return;
			}

			this.loading = true;
			this.auditList = [];

			_sProgrammingStorageService
				.auditList({ PseID: id }, this.$fun.getUserID())
				.then(r => {
					this.loading = false;
					if (r && r.data) {
						this.auditList = r.data || [];
					}
				})
				.catch(err => {
					this.loading = false;
					console.error("Error al cargar auditoría:", err);
				});
		},

		closeModal() {
			this.$emit("input", false);
			this.$emit("close");
		},

		// GIT DIFF CALCULATIONS
		isFieldChanged(itemIndex, fieldName) {
			if (itemIndex >= this.auditList.length - 1) {
				return false; // Initial version
			}
			const current = this.auditList[itemIndex];
			const previous = this.auditList[itemIndex + 1];
			if (!current || !previous) return false;

			let val1 = current[fieldName];
			let val2 = previous[fieldName];

			if (val1 === null || val1 === undefined) val1 = "";
			if (val2 === null || val2 === undefined) val2 = "";

			return String(val1) !== String(val2);
		},

		getPreviousValue(itemIndex, fieldName) {
			if (itemIndex >= this.auditList.length - 1) return null;
			const previous = this.auditList[itemIndex + 1];
			return previous ? previous[fieldName] : null;
		},

		getChangedCount(itemIndex) {
			if (itemIndex >= this.auditList.length - 1) return 0;
			const fields = [
				'TptEnrollment', 'TptDriverName', 'NumDocDriver', 'LicenseDriver',
				'PseStateName', 'PseQuantityNegotiated', 'PseQuantityJabas',
				'PseStowageQuantity', 'PsePriceStowageQuantity', 'PsePrice',
				'PseFalseFreight', 'PseDescription'
			];
			let count = 0;
			fields.forEach(f => {
				if (this.isFieldChanged(itemIndex, f)) count++;
			});
			return count;
		},

		getDiffList(itemIndex) {
			if (itemIndex >= this.auditList.length - 1) return [];

			const fieldsConfig = [
				{ field: 'TptEnrollment', label: 'Placa' },
				{ field: 'TptDriverName', label: 'Conductor' },
				{ field: 'NumDocDriver', label: 'Doc. Conductor' },
				{ field: 'LicenseDriver', label: 'Licencia' },
				{ field: 'PseStateName', label: 'Estado' },
				{ field: 'PseQuantityNegotiated', label: 'Jabas Negociadas' },
				{ field: 'PseQuantityJabas', label: 'Cant. Real Jabas' },
				{ field: 'PseStowageQuantity', label: 'Cant. Estibas' },
				{ field: 'PsePriceStowageQuantity', label: 'Precio Cant. Estibas', isCurrency: true },
				{ field: 'PsePrice', label: 'Precio Unitario', isCurrency: true },
				{ field: 'PseFalseFreight', label: 'Falso Flete', isCurrency: true },
				{ field: 'PseDescription', label: 'Observaciones' }
			];

			const diffs = [];
			fieldsConfig.forEach(cfg => {
				if (this.isFieldChanged(itemIndex, cfg.field)) {
					let oldVal = this.getPreviousValue(itemIndex, cfg.field);
					let newVal = this.auditList[itemIndex][cfg.field];

					if (cfg.isCurrency) {
						oldVal = this.formatCurrency(oldVal);
						newVal = this.formatCurrency(newVal);
					} else {
						oldVal = (oldVal === null || oldVal === undefined || oldVal === "") ? '(Vacío)' : oldVal;
						newVal = (newVal === null || newVal === undefined || newVal === "") ? '(Vacío)' : newVal;
					}

					diffs.push({
						field: cfg.field,
						label: cfg.label,
						oldVal: oldVal,
						newVal: newVal
					});
				}
			});
			return diffs;
		},

		isBlockModified(itemIndex, fieldNames) {
			return fieldNames.some(f => this.isFieldChanged(itemIndex, f));
		},

		getTimelineDotColor(index) {
			if (index === 0) return "primary";
			if (this.getChangedCount(index) > 0) return "amber darken-3";
			return "grey darken-1";
		},

		getTimelineIcon(index) {
			if (index === 0) return "mdi-star";
			if (this.getChangedCount(index) > 0) return "mdi-source-commit-local";
			return "mdi-clock-outline";
		},

		getStatusColor(state) {
			switch (parseInt(state)) {
				case 1:
					return "info";       // Asignado
				case 2:
					return "success";    // Recepcionado
				case 3:
					return "warning";    // Borrador
				case 4:
					return "error";      // Anulado
				default:
					return "grey darken-1";
			}
		},

		formatDateTime(dateStr) {
			if (!dateStr) return "-";
			if (this.$fun && typeof this.$fun.formatDateTimeView === "function") {
				return this.$fun.formatDateTimeView(dateStr);
			}
			return dateStr;
		},

		formatCurrency(val) {
			if (val === null || val === undefined || val === "") return "-";
			const num = parseFloat(val);
			if (isNaN(num)) return "-";
			return "S/ " + num.toFixed(2);
		}
	}
};
</script>

<style scoped>
.audit-modal-card {
	font-family: inherit;
}
.audit-header {
	background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}
.close-btn {
	opacity: 0.85;
	transition: opacity 0.2s ease;
}
.close-btn:hover {
	opacity: 1;
}
.bg-slate-100 {
	background-color: #f1f5f9;
}
.bg-slate-800 {
	background-color: #334155 !important;
}
.border-slate {
	border: 1px solid #475569 !important;
}
.border-b {
	border-bottom: 1px solid #e2e8f0;
}
.bg-grey-lighten {
	background-color: #f8fafc;
}
.border-card {
	border: 1.5px solid #e2e8f0;
	transition: all 0.25s ease-in-out;
}
.border-card:hover {
	border-color: #cbd5e1;
	box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.1) !important;
}
.card-highlight-changed {
	border-left: 5px solid #d97706 !important;
}
.border-card-header {
	border-bottom: 1px solid #f1f5f9;
}
.border-primary-light {
	border: 1.5px solid #bfdbfe;
}

/* FLAT & SPACIOUS DIFF STYLES (CON ESPACIO HOLGADO) */
.flat-diff-wrapper {
	background-color: #f8fafc;
}
.border-b-flat {
	border-bottom: 1px solid #f1f5f9;
}
.dot-indicator {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #d97706;
	display: inline-block;
	box-shadow: 0 0 0 2.5px #fef3c7;
}
.label-tiny {
	font-size: 10px;
	letter-spacing: 0.6px;
}
.text-strike {
	text-decoration: line-through;
	opacity: 0.85;
}
.flat-box-old {
	background-color: #fee2e2;
	border: 1px solid #fca5a5;
	display: inline-flex;
	align-items: center;
	transition: all 0.2s ease;
}
.flat-box-new {
	background-color: #dcfce7;
	border: 1px solid #86efac;
	display: inline-flex;
	align-items: center;
	transition: all 0.2s ease;
}
.shadow-xs {
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.field-modified-highlight {
	border: 1.5px solid #f59e0b !important;
	box-shadow: 0 0 8px rgba(245, 158, 11, 0.25) !important;
}
.text-changed-item {
	background-color: #fef3c7;
	padding: 2px 4px;
	border-radius: 4px;
}
.shadow-chip {
	box-shadow: 0 2px 6px rgba(217, 119, 6, 0.4);
}

.border-section {
	border: 1px solid #e2e8f0;
}
.bg-light-blue {
	background-color: #f0f9ff;
	border-color: #bae6fd;
}
.bg-light-green {
	background-color: #f0fdf4;
	border-color: #bbf7d0;
}
.bg-light-amber {
	background-color: #fffbeb;
	border-color: #fde68a;
}
.bg-amber-lighten {
	background-color: #fffbeb;
}
.border-dashed {
	border: 2px dashed #cbd5e1;
}
.border-subtle {
	border: 1px solid #e2e8f0;
}
.lh-1 {
	line-height: 1.2;
}
.caption-xs {
	font-size: 11px;
}
.italic-text {
	font-style: italic;
}
.gap-2 {
	gap: 8px;
}
</style>
